/**
 * Editing a layout, as pure functions over one.
 *
 * Everything here is total and returns a new layout rather than mutating one,
 * so undo, revert and dirty-tracking are all just holding on to an earlier
 * value. Nothing in this file knows about pointers, React or the DOM — the
 * editor is the interaction, and this is what the interaction means.
 *
 * ## Why there is no layout library behind this
 *
 * The obvious candidate was react-grid-layout, and specifically its headless
 * `/core`: collision detection, compaction, move and resize as pure functions.
 * The reason not to is that a dashboard is a *fixed canvas*, not a document
 * that packs. Nothing scrolls, the row count is fixed, and a tile stays exactly
 * where it was put. That makes the library's central service — vertical
 * compaction, pulling everything up to close gaps — not merely unnecessary but
 * wrong: it would drag every tile to the top of the screen the first time one
 * was moved.
 *
 * With compaction off and pushing off, what is left of the library is a
 * rectangle-overlap test, which is four comparisons. Against that, writing this
 * directly buys something real: the editor renders through the *same* CSS Grid
 * as the viewer, so what you arrange is literally what runs, with no second
 * layout system to keep in agreement and no library stylesheet to override.
 */

// Extension included: this is a runtime import, and Node's ESM resolver — which
// is what runs the tests — does not guess at one.
import { isKnownWidget, widgetMeta } from './dashboard.ts'
import type { DashboardLayout, LayoutEntry, WidgetConfig, WidgetKind } from './dashboard.ts'

export interface Rect {
  x: number
  y: number
  w: number
  h: number
}

/** Whether two tiles occupy any of the same cell. */
export function overlaps(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h
}

/** Whether a tile is wholly on the grid. */
export function withinGrid(r: Rect, columns: number, rows: number): boolean {
  return r.x >= 0 && r.y >= 0 && r.w >= 1 && r.h >= 1 && r.x + r.w <= columns && r.y + r.h <= rows
}

/**
 * The smallest this widget may be.
 *
 * A widget whose kind this build does not know keeps whatever size it was
 * stored at and may be moved, but is not given a minimum it might not satisfy —
 * so it can always be picked up and put somewhere out of the way.
 */
export function minSize(entry: LayoutEntry): { w: number; h: number } {
  return isKnownWidget(entry) ? widgetMeta[entry.config.kind].min : { w: 1, h: 1 }
}

/**
 * Whether `rect` is a legal home for the widget with this id.
 *
 * The widget's own current position is ignored, because moving a tile two cells
 * left must not be blocked by where it is now.
 */
export function canPlace(layout: DashboardLayout, id: string, rect: Rect): boolean {
  if (!withinGrid(rect, layout.columns, layout.rows)) return false
  return !layout.widgets.some((w) => w.id !== id && overlaps(w, rect))
}

function replace(layout: DashboardLayout, id: string, next: Partial<LayoutEntry>): DashboardLayout {
  return {
    ...layout,
    widgets: layout.widgets.map((w) => (w.id === id ? ({ ...w, ...next } as LayoutEntry) : w)),
  }
}

/**
 * Move a widget, or refuse.
 *
 * Refusing rather than shoving the occupant aside is deliberate. On a fixed
 * canvas a push has nowhere to go — it would have to cascade, and the cascade
 * ends by pushing something off the bottom of a grid that cannot grow. A move
 * that either happens or visibly does not is the one you can predict at three
 * in the morning.
 */
export function moveWidget(layout: DashboardLayout, id: string, to: { x: number; y: number }): DashboardLayout | null {
  const w = layout.widgets.find((e) => e.id === id)
  if (!w) return null
  const rect = { x: to.x, y: to.y, w: w.w, h: w.h }
  if (!canPlace(layout, id, rect)) return null
  if (w.x === to.x && w.y === to.y) return layout
  return replace(layout, id, { x: rect.x, y: rect.y })
}

/**
 * Resize a widget, or refuse.
 *
 * Clamped to the widget's declared minimum, which is what stands in for
 * breakpoints: a dashboard scales rather than reflows, so the only way to make
 * a widget unreadable is to place it too small, and this is where that is
 * prevented.
 */
export function resizeWidget(layout: DashboardLayout, id: string, size: { w: number; h: number }): DashboardLayout | null {
  const w = layout.widgets.find((e) => e.id === id)
  if (!w) return null

  const min = minSize(w)
  const rect = {
    x: w.x,
    y: w.y,
    w: Math.max(min.w, size.w),
    h: Math.max(min.h, size.h),
  }
  if (!canPlace(layout, id, rect)) return null
  if (w.w === rect.w && w.h === rect.h) return layout
  return replace(layout, id, { w: rect.w, h: rect.h })
}

/**
 * Where a move would actually land, or null if it cannot.
 *
 * Free space is the simple case. The interesting one is a drop onto another
 * tile, which on a full grid is the *only* case — a dashboard fills the screen
 * exactly, so a well-used one has no gaps at all, and a move that accepted only
 * empty space could never rearrange anything.
 *
 * So a drop onto exactly one tile of the same size trades places with it. Same
 * size is the whole condition, and it is what makes the swap free of any
 * further check: each tile lands on precisely the cells the other vacated, so
 * nothing else on the grid can be disturbed. Different sizes are refused rather
 * than guessed at, because no answer is obviously right and a wrong one moves
 * tiles the user was not touching.
 */
export function resolveMove(
  layout: DashboardLayout,
  id: string,
  to: { x: number; y: number },
): { rect: Rect; swapWith?: string } | null {
  const w = layout.widgets.find((e) => e.id === id)
  if (!w) return null

  const rect = { x: to.x, y: to.y, w: w.w, h: w.h }
  if (!withinGrid(rect, layout.columns, layout.rows)) return null

  const hit = layout.widgets.filter((o) => o.id !== id && overlaps(o, rect))
  if (hit.length === 0) return { rect }
  // Overlapping two or more tiles has no sensible reading at all.
  if (hit.length !== 1) return null

  const other = hit[0]
  if (!other || other.w !== w.w || other.h !== w.h) return null
  // Snaps onto the tile it is trading with rather than staying under the
  // pointer, so the preview shows where it will really end up.
  return { rect: { x: other.x, y: other.y, w: w.w, h: w.h }, swapWith: other.id }
}

/** Carry out a move that resolveMove has already worked out. */
export function applyMove(
  layout: DashboardLayout,
  id: string,
  move: { rect: Rect; swapWith?: string },
): DashboardLayout | null {
  const w = layout.widgets.find((e) => e.id === id)
  if (!w) return null
  if (!move.swapWith) return moveWidget(layout, id, move.rect)

  const other = layout.widgets.find((e) => e.id === move.swapWith)
  if (!other) return null
  const from = { x: w.x, y: w.y }

  return {
    ...layout,
    widgets: layout.widgets.map((e) =>
      e.id === id
        ? ({ ...e, x: other.x, y: other.y } as LayoutEntry)
        : e.id === other.id
          ? ({ ...e, x: from.x, y: from.y } as LayoutEntry)
          : e,
    ),
  }
}

/** The first free space, reading the grid the way a page is read. */
export function findFreeSpace(
  layout: DashboardLayout,
  size: { w: number; h: number },
): { x: number; y: number } | null {
  for (let y = 0; y + size.h <= layout.rows; y++) {
    for (let x = 0; x + size.w <= layout.columns; x++) {
      const rect = { x, y, w: size.w, h: size.h }
      if (!layout.widgets.some((e) => overlaps(e, rect))) return { x, y }
    }
  }
  return null
}

/**
 * A readable, stable id for a new widget.
 *
 * Not a random uuid: these end up in a stored document that gets looked at by
 * hand, and "lapTime-2" says what it is where a uuid says nothing. Uniqueness
 * only has to hold within one layout.
 */
export function nextWidgetId(layout: DashboardLayout, kind: WidgetKind): string {
  const taken = new Set(layout.widgets.map((w) => w.id))
  for (let n = 1; ; n++) {
    const id = `${kind}-${n}`
    if (!taken.has(id)) return id
  }
}

/**
 * Add a widget at its default size, or refuse because there is no room.
 *
 * Refusing is the honest answer on a canvas that cannot grow, and it is a
 * better one than silently shrinking the widget below the size at which it can
 * be read.
 */
export function addWidget(layout: DashboardLayout, kind: WidgetKind): DashboardLayout | null {
  const meta = widgetMeta[kind]

  // The comfortable size where there is room for it, the declared minimum where
  // there is not. Never smaller than the minimum: a widget too small to read is
  // worse than one that could not be added.
  let size = defaultSizeFor(kind)
  let at = findFreeSpace(layout, size)
  if (!at) {
    size = meta.min
    at = findFreeSpace(layout, size)
  }
  if (!at) return null
  const finalSize = size

  return {
    ...layout,
    widgets: [
      ...layout.widgets,
      {
        id: nextWidgetId(layout, kind),
        x: at.x,
        y: at.y,
        w: finalSize.w,
        h: finalSize.h,
        // Structured-cloned, so the new widget does not share the registry's
        // default object with every other widget of its kind.
        config: JSON.parse(JSON.stringify(meta.default)) as WidgetConfig,
      },
    ],
  }
}

/**
 * A comfortable starting size, which is the minimum plus a little room.
 *
 * A widget dropped in at exactly its minimum looks cramped and invites an
 * immediate resize; one that arrives at a sensible size usually needs none.
 */
export function defaultSizeFor(kind: WidgetKind): { w: number; h: number } {
  const min = widgetMeta[kind].min
  // The tall ones are the lists, and a list at its minimum height shows about
  // two rows, which is not what anybody wanted when they added it.
  const roomy = min.h >= 2
  return { w: Math.max(min.w, roomy ? min.w + 1 : 2), h: roomy ? min.h + 2 : Math.max(min.h, 2) }
}

export function removeWidget(layout: DashboardLayout, id: string): DashboardLayout {
  return { ...layout, widgets: layout.widgets.filter((w) => w.id !== id) }
}

/** Replace one widget's configuration, leaving its placement alone. */
export function updateWidgetConfig(layout: DashboardLayout, id: string, config: WidgetConfig): DashboardLayout {
  return {
    ...layout,
    widgets: layout.widgets.map((w) =>
      w.id === id && isKnownWidget(w) ? { ...w, config } : w,
    ),
  }
}

// ------------------------------------------------------------- persistence

/**
 * The layout as it should be written.
 *
 * Two things this does that a spread would not. It drops `undefined` fields,
 * which Firestore rejects outright — an untouched widget carries `car:
 * undefined` from parsing, and writing it would fail the whole save. And it
 * writes only the fields we own, so a stray property picked up along the way
 * cannot end up persisted.
 *
 * A widget whose kind this build does not understand is written back as it was
 * read, so opening a dashboard in an older client and saving it does not
 * quietly delete the widgets that client could not draw.
 */
export function toStoredLayout(layout: DashboardLayout): {
  columns: number
  rows: number
  widgets: Record<string, unknown>[]
} {
  return {
    columns: layout.columns,
    rows: layout.rows,
    widgets: layout.widgets.map((w) => {
      const base: Record<string, unknown> = { id: w.id, x: w.x, y: w.y, w: w.w, h: w.h }
      if (!isKnownWidget(w)) {
        base.config = { kind: w.unknownKind }
        return base
      }
      base.config = w.config as unknown as Record<string, unknown>
      if (w.car) base.car = w.car
      return base
    }),
  }
}

/**
 * Whether two layouts are the same, for deciding if Save should be enabled.
 *
 * Compared as they would be *stored*, not as they are held in memory, so a
 * difference that would never reach Firestore — an absent `car` against an
 * explicit undefined one — does not light up an unsaved-changes warning that
 * the user cannot then make go away.
 */
export function layoutsEqual(a: DashboardLayout, b: DashboardLayout): boolean {
  return JSON.stringify(toStoredLayout(a)) === JSON.stringify(toStoredLayout(b))
}
